'use client';

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSatelliteNavigationOptional } from '@to/ui';
import { Icon } from './Icon';

/**
 * Minimal vendor shape needed for the search dropdown.
 * The search API response is mapped to this interface.
 */
interface SearchDropdownVendor {
  id: number;
  companyName: string;
  logoUrl?: string | null;
  categories?: string[];
  city?: string;
  state?: string;
}

export interface SearchBarProps {
  /** API endpoint that returns `{ vendors: [...] }`. Default: '/api/vendors' */
  searchApiUrl?: string;
  /** Path prefix for vendor detail links. Default: '/vendors' */
  vendorDetailPath?: string;
  /** Input placeholder text. Default: 'Search vendors' */
  placeholder?: string;
  /** Color variant. 'dark' for white text on dark bg (hero), 'light' for dark text on white bg (cards). Default: 'dark' */
  variant?: 'dark' | 'light';
  /** When provided, renders a search button that navigates to this path with ?q= param. */
  searchPagePath?: string;
}

/**
 * Instant search bar powered by a vendor search API endpoint.
 * Uses debounced input (300ms) and displays results in a dropdown.
 * Styled for the glassmorphic hero search bar with white text.
 */
export function SearchBar({
  searchApiUrl = '/api/vendors',
  vendorDetailPath = '/vendors',
  placeholder = 'Search vendors',
  variant = 'dark',
  searchPagePath,
}: SearchBarProps) {
  const router = useRouter();
  const navigation = useSatelliteNavigationOptional();
  const isLight = variant === 'light';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchDropdownVendor[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const inputId = useId();
  const listboxId = useId();

  const handleSearchSubmit = searchPagePath
    ? () => {
        setIsOpen(false);
        const targetHref = query.trim()
          ? `${searchPagePath}?q=${encodeURIComponent(query.trim())}`
          : searchPagePath;
        const started =
          navigation?.beginNavigation({
            routeTemplate: '/vendors',
            navigationType: 'data_action',
            targetKey: targetHref,
            targetHref,
          }) ?? true;
        if (started) router.push(targetHref);
      }
    : undefined;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const handleSearch = useCallback(
    (value: string) => {
      setQuery(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!value.trim()) {
        setResults([]);
        setIsOpen(false);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `${searchApiUrl}?q=${encodeURIComponent(value)}&pageSize=8`,
          );
          if (!res.ok) throw new Error('Search failed');
          const data = await res.json();

          // Map API response to dropdown shape
          const vendors: SearchDropdownVendor[] = (data.vendors || []).map(
            (v: Record<string, unknown>) => ({
              id: v.id as number,
              companyName: v.companyName as string,
              logoUrl: (v.logoUrl ?? v.logo ?? null) as string | null,
              categories: (v.categories ?? []) as string[],
              city: (v.city ?? '') as string,
              state: (v.state ?? '') as string,
            }),
          );

          setResults(vendors);
          setIsOpen(true);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [searchApiUrl],
  );

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-1 flex items-center">
      {/* Search input */}
      <div className="flex flex-1 items-center px-6">
        <label htmlFor={inputId} className="sr-only">
          {placeholder}
        </label>
        <input
          id={inputId}
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && handleSearchSubmit) handleSearchSubmit();
          }}
          placeholder={placeholder}
          className={`h-[52px] w-full border-0 bg-transparent text-lg font-medium outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2 ${
            isLight ? 'placeholder:text-[var(--brand-input-placeholder)]' : 'placeholder:text-white/70'
          }`}
          style={{
            color: isLight ? 'var(--brand-text)' : '#FFFFFF',
            fontFamily: 'var(--brand-font-body)',
          }}
          aria-label={placeholder}
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          role="combobox"
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className={`ml-2 flex-shrink-0 rounded-full p-1 transition-all duration-150 ${
              isLight ? 'hover:bg-[var(--brand-surface-subtle)]' : 'hover:bg-white/20'
            }`}
            style={{ color: isLight ? 'var(--brand-text-muted)' : 'rgba(255, 255, 255, 0.8)' }}
            aria-label="Clear search"
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>

      {/* Search submit button — only shown when searchPagePath is provided */}
      {handleSearchSubmit && (
        <button
          type="button"
          disabled={navigation?.isPending}
          aria-busy={navigation?.isPending}
          onClick={handleSearchSubmit}
          className="mr-1 flex flex-shrink-0 items-center justify-center rounded-full transition-opacity duration-150 hover:opacity-90"
          style={{ backgroundColor: 'var(--brand-primary)', width: '52px', height: '52px' }}
          aria-label="Search all vendors"
        >
          <Icon name="search" size={20} style={{ color: '#FFFFFF' }} />
        </button>
      )}

      {/* Results dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-4 overflow-hidden rounded-2xl border"
          style={{
            borderColor: 'var(--brand-card-border)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            backgroundColor: '#FFFFFF',
          }}
          role="listbox"
          id={listboxId}
        >
          {isSearching && results.length === 0 ? (
            <div
              className="px-4 py-8 text-center text-sm"
              style={{ color: 'var(--brand-text-muted)' }}
            >
              <div
                className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-current"
                style={{ borderTopColor: 'transparent' }}
              />
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div
              className="px-4 py-8 text-center text-sm"
              style={{ color: 'var(--brand-text-muted)' }}
            >
              No vendors found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((vendor) => (
                <li key={vendor.id}>
                  <Link
                    href={`${vendorDetailPath}/${vendor.id}`}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[var(--brand-surface-subtle)]"
                    style={{ color: 'var(--brand-text)' }}
                    role="option"
                    aria-selected={false}
                  >
                    {/* Vendor logo */}
                    <div
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border"
                      style={{
                        borderColor: 'var(--brand-card-border, #f3f4f6)',
                        backgroundColor: 'var(--brand-surface-subtle, #f9fafb)',
                      }}
                    >
                      {vendor.logoUrl ? (
                        <Image
                          src={vendor.logoUrl}
                          alt={`${vendor.companyName} logo`}
                          width={40}
                          height={40}
                          className="h-full w-full object-contain p-1"
                        />
                      ) : (
                        <span
                          className="text-xs font-semibold"
                          style={{ color: 'var(--brand-input-placeholder)' }}
                        >
                          {vendor.companyName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Vendor info */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {vendor.companyName}
                      </p>
                      <p
                        className="truncate text-xs"
                        style={{ color: 'var(--brand-text-muted)' }}
                      >
                        {[
                          vendor.categories?.[0],
                          [vendor.city, vendor.state].filter(Boolean).join(', '),
                        ]
                          .filter(Boolean)
                          .join(' \u00B7 ')}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
